/* eslint-disable no-undef */
import { AddIcon } from "@chakra-ui/icons";
import { FormControl, Switch, Checkbox, IconButton, Input, Box, Button } from "@chakra-ui/react";
import { FormikProps, Field, FieldArray } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import hotToast from "react-hot-toast";
import { BsTrash } from "react-icons/bs";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { useSelector } from "react-redux";
import { SideBarFormArguments } from "../../models/form.model";
import {
    selectCampaignTemplates,
    selectCampaigns,
    selectCampaignsLoading
} from "../../store/slices/campaign.slice";
import { selectCatalogues } from "../../store/slices/catalogue.slice";
import { selectClients } from "../../store/slices/client.slice";
import { selectEvents } from "../../store/slices/event.slice";
import { selectTemplate } from "../../store/slices/template.slice";
import { ICampaign } from "../../types/campaign";
import { TemplateMetadataModel } from "../../types/template";
import {
    ITransactionCoordinatorCatalogue,
    ITransactionCoordinatorEvent
} from "../../types/transaction-coordinator";
import { useHttpClient } from "../../utils/http.utils";
import { formatSnakeCaseToTitleCase } from "../../utils/string";
import FileUploadControl from "../form/FileUploadControl";
import FormikSelect from "../form/FormikSelect";
import MultipleFilesUploadControl from "../form/MultipleFilesUploadControl";
import Card from "./ObjectCard";
import ObjectModal from "./ObjectInputControl";
import timezones from "./timezone";

// Add a global state for tracking columns
type ColumnState = {
    [argumentKey: string]: string[];
};

// Create a global state for tracking columns
let globalColumnState: ColumnState = {};

type ToolFormControlProps = {
    name: string;
    argument: SideBarFormArguments;
    showConvertToUserInputCheckbox?: boolean;
    onConvertToUserInput?: (checked: boolean) => void;
    formikProps: FormikProps<any>;
};

const FormikFormInputControl: React.FC<ToolFormControlProps> = ({
    name,
    argument,
    formikProps,
    showConvertToUserInputCheckbox: showPersistCheckbox,
    onConvertToUserInput: onPersist
}) => {
    const isInvalid = !!formikProps.errors[name] && !!formikProps.touched[name];
    const [showDescription, setShowDescription] = useState<Record<string, boolean>>({});
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const templates = useSelector(selectTemplate);
    const events = useSelector(selectEvents);
    const catalogues = useSelector(selectCatalogues);
    const campaignTemplates = useSelector(selectCampaignTemplates);
    const campaigns = useSelector(selectCampaigns);
    const campaignsLoading = useSelector(selectCampaignsLoading);
    const clients = useSelector(selectClients);
    const { listCampaignsApi } = useHttpClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [objectResult, setObjectResult] = useState<Record<string, any> | null>(null);
    // New state to track columns from templates
    const [columnState, setColumnState] = useState<ColumnState>(globalColumnState);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);

    // Log when columnState changes
    useEffect(() => {
        // Update global state to ensure it's always in sync with component state
        // Merge instead of replacing to maintain data from other components
        globalColumnState = { ...globalColumnState, ...columnState };
    }, [columnState]);

    // Auto-expand textarea effect
    useEffect(() => {
        if (textareaRef.current && argument.type === "text_area") {
            // Reset height to auto to get the correct scrollHeight
            textareaRef.current.style.height = "auto";

            // Set the height based on scrollHeight
            const scrollHeight = textareaRef.current.scrollHeight;
            const minHeight = 100; // Minimum height in pixels
            const maxHeight = 400; // Maximum height in pixels

            const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));
            textareaRef.current.style.height = `${newHeight}px`;

            // Show scrollbar if content exceeds max height
            if (scrollHeight > maxHeight) {
                textareaRef.current.style.overflowY = "auto";
            } else {
                textareaRef.current.style.overflowY = "hidden";
            }
        }
    }, [formikProps.values[name], argument.type]);

    const clientOptions = useMemo(() => {
        return clients.map((client) => ({
            label: client.title,
            value: client.id
        }));
    }, [clients]);

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        // Clear the modal input values before closing
        if (argument.key_placeholder !== undefined) {
            formikProps.setFieldValue(argument.key_placeholder, "");
            if (argument.value_schema) {
                formikProps.setFieldValue("value_schema", "");
            } else if (argument.properties) {
                Object.keys(argument.properties).forEach((key) => {
                    formikProps.setFieldValue(key, "");
                });
            }
        } else {
            Object.keys(argument.properties || {}).forEach((key) => {
                formikProps.setFieldValue(key, "");
            });
        }
        setIsModalOpen(false);
        setIsEditing(false);
        setEditingKey(null);
    };

    const templateOptions = useMemo(() => {
        return templates.map((template: TemplateMetadataModel) => ({
            label: template.name,
            value: template
        }));
    }, [templates]);

    const eventOptions = useMemo(() => {
        return events.map((event: ITransactionCoordinatorEvent) => ({
            label: event.event_name,
            value: event
        }));
    }, [events]);

    const catalogueOptions = useMemo(() => {
        return catalogues.map((catalogue: ITransactionCoordinatorCatalogue) => ({
            label: catalogue.transaction_coordinator_event?.event_name,
            value: catalogue
        }));
    }, [catalogues]);

    const campaignTemplateOptions = useMemo(() => {
        return campaignTemplates.map((campaignTemplate: ICampaign) => ({
            label: campaignTemplate.campaign_name,
            value: campaignTemplate.id
        }));
    }, [campaignTemplates]);

    useEffect(() => {
        /**
         * Clear the input field if form value is empty
         */
        if (textAreaRef.current) {
            // If the field value does not exist, clear the form+
            if (!formikProps.values[name]) {
                textAreaRef.current.value = "";
                textAreaRef.current.style.height = "40px";
                textAreaRef.current.style.overflow = "hidden";
            } else {
                const contentHeight = textAreaRef.current.scrollHeight;

                if (contentHeight >= 200) {
                    textAreaRef.current.style.overflowY = "auto";
                    textAreaRef.current.style.height = "200px";
                } else {
                    textAreaRef.current.style.height = contentHeight + "px";
                    textAreaRef.current.style.overflow = "hidden";
                }
            }
        }
    }, [name, formikProps.values]);

    useEffect(() => {
        const initialValue = formikProps.values[argument.key];
        if (initialValue) {
            setObjectResult(initialValue);
        }
    }, [argument.key, formikProps.values[argument.key]]);

    const validateRequired = (value: string, label: string) => {
        let error;

        if (!value) {
            return label ? `${label} is required` : "Required";
        }

        return error;
    };

    const showFileUPloadError = () => {
        hotToast.error(
            `Cannot upload file for the ${argument.title || argument.label} argument. Please try again`
        );
    };

    const onPersistHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (typeof onPersist === "function") {
            onPersist(e.target.checked);
        }
    };

    const hasItem = !!formikProps.values[name];

    // Initialize column state when already selected on load
    useEffect(() => {
        if (argument.type === "single_private_structured_db") {
            const selectedId = formikProps.values[name];
            if (selectedId) {
                const selectedTemplate = templates.find((template) => template.id === selectedId);
                if (
                    selectedTemplate &&
                    selectedTemplate.columns &&
                    typeof selectedTemplate.columns === "object"
                ) {
                    const columnNames = Object.keys(selectedTemplate.columns);

                    // Update the column state with preloaded data
                    // Also update globalColumnState immediately to ensure it's available to other components
                    const newState = {
                        ...columnState,
                        [argument.key]: columnNames // Use argument.key instead of name
                    };

                    // Update both states - merge with global state rather than replace
                    globalColumnState = { ...globalColumnState, ...newState };
                    setColumnState(newState);
                }
            }
        }

        // Initialize the field value for adf_template_columns
        if (argument.type === "adf_template_columns" && !formikProps.values[name]) {
            formikProps.setFieldValue(name, {});
        }
    }, [argument.type, argument.key, formikProps.values[name], templates, name]);

    switch (argument.type) {
        case "boolean":
            return (
                <FormControl className="pt-[16px] mt-[12px] pb-[10px]">
                    <div className="flex justify-between items-center">
                        <div className="flex gap-[5px] items-center">
                            <p
                                className={`text-[13px] text-white font-medium flex items-center gap-[4px]`}
                            >
                                <span>{argument.title || argument.label}</span>
                                {argument.required && (
                                    <span className="text-red-400 text-[10.8px]">(Required)</span>
                                )}
                            </p>
                            {!!argument.description && (
                                <IconButton
                                    icon={<IoIosInformationCircleOutline size="18px" />}
                                    aria-label={"Description"}
                                    title="Show Description"
                                    className="cursor-pointer"
                                    size="xs"
                                    variant={"ghost"}
                                    onClick={() =>
                                        setShowDescription((prev) => ({
                                            ...prev,
                                            [name]: !prev[name]
                                        }))
                                    }
                                />
                            )}
                        </div>

                        <Field name={name}>
                            {({ field }) => (
                                <Switch
                                    {...field}
                                    isChecked={
                                        formikProps.values[name] !== undefined
                                            ? formikProps.values[name]
                                            : false
                                    }
                                    onChange={(e) =>
                                        formikProps.setFieldValue(name, e.target.checked)
                                    }
                                    isDisabled={argument.parameter_type === "user"}
                                />
                            )}
                        </Field>
                    </div>
                    {argument.description && showDescription[name] && (
                        <p className="text-[13px] mt-[4px] font-normal text-neutral-400">
                            {argument.description ||
                                `Toggle ${argument.title || argument.label} status`}
                        </p>
                    )}
                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );
        case "file":
            return (
                <FormControl mb={2}>
                    <div className="form-control-header">
                        <div className="mb-[8px]">
                            <div className="flex justify-between items-center">
                                <p
                                    className={`text-[13px] text-white font-medium flex items-center gap-[4px]`}
                                >
                                    <span>{argument.title || argument.label}</span>
                                    {argument.required && (
                                        <span className="text-red-400 text-[10.8px]">
                                            (Required)
                                        </span>
                                    )}
                                </p>
                                {!!argument.description && (
                                    <IconButton
                                        icon={<IoIosInformationCircleOutline size="18px" />}
                                        aria-label={"Description"}
                                        title="Show Description"
                                        className="cursor-pointer"
                                        size="xs"
                                        variant={"ghost"}
                                        onClick={() =>
                                            setShowDescription((prev) => ({
                                                ...prev,
                                                [name]: !prev[name]
                                            }))
                                        }
                                    />
                                )}
                            </div>
                            {argument.description && showDescription[name] && (
                                <p className="text-[13px] font-normal text-neutral-400">
                                    {argument.description}
                                </p>
                            )}
                        </div>
                        {/* {formikProps.values[name] && <button
                            className="persist-btn text-[12px] font-medium text-[#2196f3] px-2 py-[1px] h-[22px] hover:bg-gray-600 rounded"
                        >Persist</button>} */}
                    </div>
                    {/* TODO: USER THE the field hook */}
                    <FileUploadControl
                        uploadType="tool_argument"
                        onComplete={({ file_name }) => formikProps.setFieldValue(name, file_name)}
                        onError={showFileUPloadError}
                        isDisabled={argument.parameter_type === "user"}
                    />
                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );
        case "files_array":
            return (
                <FormControl mb={2}>
                    <div className="form-control-header">
                        <div className="mb-[8px]">
                            <div className="flex justify-between items-center">
                                <p
                                    className={`text-[13px] text-white font-medium flex items-center gap-[4px]`}
                                >
                                    <span>{argument.title || argument.label}</span>
                                    {argument.required && (
                                        <span className="text-red-400 text-[10.8px]">
                                            (Required)
                                        </span>
                                    )}
                                </p>
                                {!!argument.description && (
                                    <IconButton
                                        icon={<IoIosInformationCircleOutline size="18px" />}
                                        aria-label={"Description"}
                                        title="Show Description"
                                        className="cursor-pointer"
                                        size="xs"
                                        variant={"ghost"}
                                        onClick={() =>
                                            setShowDescription((prev) => ({
                                                ...prev,
                                                [name]: !prev[name]
                                            }))
                                        }
                                    />
                                )}
                            </div>
                            {argument.description && showDescription[name] && (
                                <p className="text-[13px] font-normal text-neutral-400">
                                    {argument.description}
                                </p>
                            )}
                        </div>
                        {/* {formikProps.values[name] && <button
                                className="persist-btn text-[12px] font-medium text-[#2196f3] px-2 py-[1px] h-[22px] hover:bg-gray-600 rounded"
                            >Persist</button>} */}
                    </div>
                    {/* TODO: USER THE the field hook */}
                    <MultipleFilesUploadControl
                        uploadType="tool_argument"
                        onComplete={({ file_names }) => formikProps.setFieldValue(name, file_names)}
                        onError={showFileUPloadError}
                        isDisabled={argument.parameter_type === "user"}
                    />
                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );
        case "private_structured_db":
            return (
                <FormControl mb={6} isInvalid={isInvalid}>
                    <div className="mb-[8px]">
                        <div className="flex justify-between items-center">
                            <p
                                className={`text-[13px] text-white font-medium flex items-center gap-[4px]`}
                            >
                                <span>{argument.title || argument.label}</span>
                                {argument.required && (
                                    <span className="text-red-400 text-[10.8px]">(Required)</span>
                                )}
                            </p>
                            {!!argument.description && (
                                <IconButton
                                    icon={<IoIosInformationCircleOutline size="18px" />}
                                    aria-label={"Description"}
                                    title="Show Description"
                                    className="cursor-pointer"
                                    size="xs"
                                    variant={"ghost"}
                                    onClick={() =>
                                        setShowDescription((prev) => ({
                                            ...prev,
                                            [name]: !prev[name]
                                        }))
                                    }
                                />
                            )}
                        </div>
                        {argument.description && showDescription[name] && (
                            <p className="text-[13px] font-normal text-neutral-400">
                                {argument.description}
                            </p>
                        )}
                    </div>
                    <Field
                        name={name}
                        {...(argument.required
                            ? {
                                  validate: (value) => validateRequired(value, argument.label)
                              }
                            : {})}
                    >
                        {({ field }) => (
                            <FormikSelect
                                {...field}
                                id={name}
                                placeholder={`Select ${argument.title || argument.label}`}
                                options={templateOptions}
                                isMulti={true}
                                className={`${isInvalid && "border-[#FC8181]"}`}
                                onChange={(selectedOptions) =>
                                    formikProps.setFieldValue(name, selectedOptions)
                                }
                                onBlur={formikProps.handleBlur}
                                value={formikProps.values[name]}
                            />
                        )}
                    </Field>
                    {isInvalid && (
                        <p className="form-errors text-[14px] mt-[4px] text-red-300">
                            {formikProps.errors[name]?.toString()}
                        </p>
                    )}
                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );
        case "single_private_structured_db":
            return (
                <FormControl mb={6} isInvalid={isInvalid}>
                    <div className="mb-[8px]">
                        <div className="flex justify-between items-center">
                            <p
                                className={`text-[13px] text-white font-medium flex items-center gap-[4px]`}
                            >
                                <span>{argument.title || argument.label}</span>
                                {argument.required && (
                                    <span className="text-red-400 text-[10.8px]">(Required)</span>
                                )}
                            </p>
                            {!!argument.description && (
                                <IconButton
                                    icon={<IoIosInformationCircleOutline size="18px" />}
                                    aria-label={"Description"}
                                    title="Show Description"
                                    className="cursor-pointer"
                                    size="xs"
                                    variant={"ghost"}
                                    onClick={() =>
                                        setShowDescription((prev) => ({
                                            ...prev,
                                            [name]: !prev[name]
                                        }))
                                    }
                                />
                            )}
                        </div>
                        {argument.description && showDescription[name] && (
                            <p className="text-[13px] font-normal text-neutral-400">
                                {argument.description}
                            </p>
                        )}
                    </div>
                    <Field
                        name={name}
                        {...(argument.required
                            ? {
                                  validate: (value) =>
                                      validateRequired(value, argument.title || argument.label)
                              }
                            : {})}
                    >
                        {({ field }) => {
                            // Find the selected template option based on stored ID
                            const selectedId = formikProps.values[name];
                            const selectedOption = selectedId
                                ? templateOptions.find((opt) => opt.value.id === selectedId)
                                : null;

                            return (
                                <FormikSelect
                                    {...field}
                                    id={name}
                                    placeholder={`Select ${argument.title || argument.label}`}
                                    options={templateOptions}
                                    className={`${isInvalid && "border-[#FC8181]"}`}
                                    isDisabled={argument.is_disabled}
                                    onChange={(selected) => {
                                        // Store only the ID from the selected template
                                        if (selected && typeof selected === "object") {
                                            const option = selected as {
                                                value: { id: string; columns?: any };
                                            };
                                            formikProps.setFieldValue(name, option.value.id);

                                            // Extract column names (keys) from the selected template
                                            if (
                                                option.value.columns &&
                                                typeof option.value.columns === "object"
                                            ) {
                                                const columnNames = Object.keys(
                                                    option.value.columns
                                                );

                                                // Update using spread to preserve other entries
                                                const newState = {
                                                    ...columnState,
                                                    [argument.key]: columnNames
                                                };

                                                // Update both states together - merge rather than replace global state
                                                globalColumnState = {
                                                    ...globalColumnState,
                                                    ...newState
                                                };
                                                setColumnState(newState);
                                            }
                                        } else {
                                            formikProps.setFieldValue(name, null);

                                            // Clear only this argument's entry from column state
                                            const newState = { ...columnState };
                                            delete newState[argument.key];

                                            // Update local state
                                            setColumnState(newState);

                                            // Update global state preserving other entries
                                            const updatedGlobalState = { ...globalColumnState };
                                            delete updatedGlobalState[argument.key];
                                            globalColumnState = updatedGlobalState;
                                        }
                                    }}
                                    onBlur={formikProps.handleBlur}
                                    value={selectedOption}
                                />
                            );
                        }}
                    </Field>
                    {isInvalid && (
                        <p className="form-errors text-[14px] mt-[4px] text-red-300">
                            {formikProps.errors[name]?.toString()}
                        </p>
                    )}
                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );
        case "campaign_templates":
            return (
                <FormControl mb={6} isInvalid={isInvalid}>
                    <div className="mb-[8px]">
                        <div className="flex justify-between items-center">
                            <p
                                className={`text-[13px] text-white font-medium flex items-center gap-[4px]`}
                            >
                                <span>{argument.title || argument.label}</span>
                                {argument.required && (
                                    <span className="text-red-400 text-[10.8px]">(Required)</span>
                                )}
                            </p>
                            {!!argument.description && (
                                <IconButton
                                    icon={<IoIosInformationCircleOutline size="18px" />}
                                    aria-label={"Description"}
                                    title="Show Description"
                                    className="cursor-pointer"
                                    size="xs"
                                    variant={"ghost"}
                                    onClick={() =>
                                        setShowDescription((prev) => ({
                                            ...prev,
                                            [name]: !prev[name]
                                        }))
                                    }
                                />
                            )}
                        </div>
                        {argument.description && showDescription[name] && (
                            <p className="text-[13px] font-normal text-neutral-400">
                                {argument.description}
                            </p>
                        )}
                    </div>
                    <Field
                        name={name}
                        {...(argument.required
                            ? {
                                  validate: (value) => validateRequired(value, argument.label)
                              }
                            : {})}
                    >
                        {({ field }) => {
                            // Retrieve the stored IDs from Formik values
                            const selectedIds = formikProps.values[name] || [];

                            // Reconstruct the selected options from campaignTemplateOptions
                            const selectedOptions = campaignTemplateOptions.filter((option: any) =>
                                selectedIds.includes(option.value)
                            );

                            return (
                                <FormikSelect
                                    {...field}
                                    id={name}
                                    placeholder={`Select ${argument.title || argument.label}`}
                                    options={campaignTemplateOptions}
                                    isMulti={true}
                                    className={`${isInvalid && "border-[#FC8181]"}`}
                                    onChange={(options: any) => {
                                        if (options.length <= 10) {
                                            // Map selected options to an array of ids only.
                                            const ids = options.map((option: any) => option.value);
                                            formikProps.setFieldValue(name, ids);
                                        }
                                    }}
                                    onBlur={formikProps.handleBlur}
                                    value={selectedOptions}
                                />
                            );
                        }}
                    </Field>
                    {isInvalid && (
                        <p className="form-errors text-[14px] mt-[4px] text-red-300">
                            {formikProps.errors[name]?.toString()}
                        </p>
                    )}
                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );
        case "campaigns":
            return (
                <FormControl mb={6} isInvalid={isInvalid}>
                    <div className="mb-[8px]">
                        <div className="flex justify-between items-center">
                            <p
                                className={`text-[13px] text-white font-medium flex items-center gap-[4px]`}
                            >
                                <span>{argument.title || argument.label}</span>
                                {argument.required && (
                                    <span className="text-red-400 text-[10.8px]">(Required)</span>
                                )}
                            </p>
                            {!!argument.description && (
                                <IconButton
                                    icon={<IoIosInformationCircleOutline size="18px" />}
                                    aria-label={"Description"}
                                    title="Show Description"
                                    className="cursor-pointer"
                                    size="xs"
                                    variant={"ghost"}
                                    onClick={() =>
                                        setShowDescription((prev) => ({
                                            ...prev,
                                            [name]: !prev[name]
                                        }))
                                    }
                                />
                            )}
                        </div>
                        {argument.description && showDescription[name] && (
                            <p className="text-[13px] font-normal text-neutral-400">
                                {argument.description}
                            </p>
                        )}
                    </div>
                    <Field
                        name={name}
                        {...(argument.required
                            ? {
                                  validate: (value) => validateRequired(value, argument.label)
                              }
                            : {})}
                    >
                        {({ field }) => {
                            // Get the dependent argument that provides the template ID
                            const dependsOn = argument.depends_on;
                            const templateId = dependsOn ? formikProps.values[dependsOn] : null;

                            // Generate campaign options from the campaigns state
                            const campaignOptions = useMemo(() => {
                                return campaigns.map((campaign) => ({
                                    label: campaign.campaign_name,
                                    value: campaign.id
                                }));
                            }, [campaigns]);

                            // Fetch campaigns when template ID changes
                            useEffect(() => {
                                if (templateId) {
                                    listCampaignsApi({ template: templateId }).catch((error) => {
                                        console.error("Failed to fetch campaigns:", error);
                                        hotToast.error("Failed to load campaigns");
                                    });
                                }
                            }, [templateId]);

                            // Retrieve the stored IDs from Formik values
                            const selectedIds = formikProps.values[name] || [];

                            // Reconstruct the selected options from campaignOptions
                            const selectedOptions = campaignOptions.filter((option: any) =>
                                selectedIds.includes(option.value)
                            );

                            return (
                                <FormikSelect
                                    {...field}
                                    id={name}
                                    placeholder={
                                        !templateId
                                            ? "Please select a template first"
                                            : campaignsLoading
                                              ? "Loading campaigns..."
                                              : `Select ${argument.title || argument.label}`
                                    }
                                    options={campaignOptions}
                                    isMulti={true}
                                    className={`${isInvalid && "border-[#FC8181]"}`}
                                    isDisabled={
                                        !templateId ||
                                        campaignsLoading ||
                                        argument.parameter_type === "user"
                                    }
                                    onChange={(options: any) => {
                                        // Map selected options to an array of ids only.
                                        const ids = options.map((option: any) => option.value);
                                        formikProps.setFieldValue(name, ids);
                                    }}
                                    onBlur={formikProps.handleBlur}
                                    value={selectedOptions}
                                />
                            );
                        }}
                    </Field>
                    {isInvalid && (
                        <p className="form-errors text-[14px] mt-[4px] text-red-300">
                            {formikProps.errors[name]?.toString()}
                        </p>
                    )}
                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );
        case "memory_map_columns": {
            const memoryDependsOn = argument.depends_on;
            console.log("memoryDependsOn", memoryDependsOn);
            const memoryColumns =
                memoryDependsOn &&
                (columnState[memoryDependsOn] || globalColumnState[memoryDependsOn]);
            console.log("columnState", columnState);
            console.log("globalColumnState", globalColumnState);
            console.log("memoryColumns", memoryColumns);
            const memoryColumnOptions = memoryColumns
                ? memoryColumns.map((column) => ({ label: column, value: column }))
                : [];
            console.log("memoryColumnOptions", memoryColumnOptions);
            const memoryIsInvalid = !!formikProps.errors[name] && !!formikProps.touched[name];
            const memoryCurrentValue = formikProps.values[name] || "";
            console.log("memoryCurrentValue", memoryCurrentValue);

            return (
                <FormControl mb={6} isInvalid={memoryIsInvalid}>
                    <div className="mb-[8px]">
                        <div className="flex justify-between items-center">
                            <p
                                className={`text-[13px] text-white font-medium flex items-center gap-[4px]`}
                            >
                                <span>{formatSnakeCaseToTitleCase(argument.label)}</span>
                                {argument.required && (
                                    <span className="text-red-400 text-[10.8px]">(Required)</span>
                                )}
                            </p>
                            {!!argument.description && (
                                <IconButton
                                    icon={<IoIosInformationCircleOutline size="18px" />}
                                    aria-label={"Description"}
                                    title="Show Description"
                                    className="cursor-pointer"
                                    size="xs"
                                    variant={"ghost"}
                                    onClick={() =>
                                        setShowDescription((prev) => ({
                                            ...prev,
                                            [name]: !prev[name]
                                        }))
                                    }
                                />
                            )}
                        </div>
                        {argument.description && showDescription[name] && (
                            <p className="text-[13px] font-normal text-neutral-400">
                                {argument.description}
                            </p>
                        )}
                    </div>

                    <Field
                        name={name}
                        {...(argument.required
                            ? {
                                  validate: (value: string) => {
                                      if (!value) {
                                          return argument.label
                                              ? `${argument.title || argument.label} is required`
                                              : "Required";
                                      }
                                      return undefined;
                                  }
                              }
                            : {})}
                    >
                        {() => (
                            <FormikSelect
                                id={name}
                                placeholder="Select a column"
                                options={memoryColumnOptions}
                                className="w-full"
                                name={name}
                                onChange={(selected: any) => {
                                    formikProps.setFieldValue(name, selected ? selected.value : "");
                                }}
                                value={memoryColumnOptions.find(
                                    (opt) => opt.value === memoryCurrentValue
                                )}
                            />
                        )}
                    </Field>

                    {memoryIsInvalid && (
                        <p className="form-errors text-[14px] mt-[4px] text-red-300">
                            {formikProps.errors[name]?.toString()}
                        </p>
                    )}
                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );
        }
        case "template_adf_mapping":
            return (
                <FormControl mb={6} isInvalid={isInvalid}>
                    <div className="mb-[8px]">
                        <div className="flex justify-between items-center">
                            <p
                                className={`text-[13px] text-white font-medium flex items-center gap-[4px]`}
                            >
                                <span>{argument.title || argument.label}</span>
                                {argument.required && (
                                    <span className="text-red-400 text-[10.8px]">(Required)</span>
                                )}
                            </p>
                            {!!argument.description && (
                                <IconButton
                                    icon={<IoIosInformationCircleOutline size="18px" />}
                                    aria-label={"Description"}
                                    title="Show Description"
                                    className="cursor-pointer"
                                    size="xs"
                                    variant={"ghost"}
                                    onClick={() =>
                                        setShowDescription((prev) => ({
                                            ...prev,
                                            [name]: !prev[name]
                                        }))
                                    }
                                />
                            )}
                        </div>
                        {argument.description && showDescription[name] && (
                            <p className="text-[13px] font-normal text-neutral-400">
                                {argument.description}
                            </p>
                        )}
                    </div>
                    <Field
                        name={name}
                        {...(argument.required
                            ? {
                                  validate: (value) => validateRequired(value, argument.label)
                              }
                            : {})}
                    >
                        {() => {
                            // Type definition for a mapping row
                            type AdfMappingRow = { column: string; adf_field: string };
                            type MappingRows = { [key: string]: AdfMappingRow };

                            // Initialize internal state to track UI rows
                            const [mappingRows, setMappingRows] = useState<MappingRows>({});

                            // Get the dependent argument name that provides the columns
                            const dependsOn = argument.depends_on;

                            // Try to get columns from both local state and global state
                            const columns =
                                dependsOn &&
                                (columnState[dependsOn] || globalColumnState[dependsOn]);

                            // Generate column options from the columns
                            const columnOptions = columns
                                ? columns.map((column) => ({
                                      label: column,
                                      value: column
                                  }))
                                : [];

                            // Function to recursively flatten the ADF structure
                            const flattenAdfStructure = (
                                obj: any,
                                path = "",
                                result: Array<{ label: string; value: string }> = []
                            ): Array<{ label: string; value: string }> => {
                                if (!obj || typeof obj !== "object") {
                                    return result;
                                }

                                // Format display label to be more readable
                                const formatLabel = (p: string): string => {
                                    if (!p) {
                                        return "";
                                    }
                                    // Handle parameter notation separately
                                    if (p.includes("[")) {
                                        const basePath = p.split("[")[0];
                                        const paramPart = p.substring(p.indexOf("["));
                                        const formattedBase = basePath.split(".").join(" › ");
                                        return `${formattedBase} ${paramPart}`;
                                    }
                                    return p.split(".").join(" › ");
                                };

                                // Handle complex nested structures
                                if (obj.value && obj.value.items) {
                                    // This is a container with child items
                                    Object.keys(obj.value.items).forEach((key) => {
                                        const newPath = path ? `${path}.${key}` : key;

                                        // Add this level
                                        result.push({
                                            label: formatLabel(newPath),
                                            value: newPath
                                        });

                                        // Recursively process children
                                        flattenAdfStructure(obj.value.items[key], newPath, result);
                                    });
                                }

                                // Handle parameters (add as field[param=value])
                                if (obj.parameters) {
                                    Object.keys(obj.parameters).forEach((paramKey) => {
                                        const param = obj.parameters[paramKey];
                                        if (param.enum && Array.isArray(param.enum)) {
                                            param.enum.forEach((enumValue) => {
                                                const paramPath = `${path}[${paramKey}=${enumValue}]`;
                                                result.push({
                                                    label: formatLabel(paramPath),
                                                    value: paramPath
                                                });
                                            });
                                        }
                                    });
                                }

                                // Handle leaf nodes with simple values
                                if (obj.value && obj.value.type === "string") {
                                    result.push({
                                        label: formatLabel(path),
                                        value: path
                                    });
                                }

                                return result;
                            };

                            // Generate options from adf_structure if available
                            const adfStructureOptions = argument.adf_structure
                                ? flattenAdfStructure(argument.adf_structure)
                                : [];

                            const isDropdownDisabled =
                                !dependsOn || !columns || columns.length === 0;

                            // Initialize UI state from current field value (if any)
                            useEffect(() => {
                                // Only initialize once
                                if (Object.keys(mappingRows).length === 0) {
                                    const currentValue = formikProps.values[name] || {};
                                    const newRows: MappingRows = {};

                                    // For each column:adfField pair, create a UI row
                                    Object.entries(currentValue).forEach(([column, adfField]) => {
                                        const rowId = `row-${Math.random().toString(36).substr(2, 9)}`;
                                        newRows[rowId] = {
                                            column,
                                            adf_field: adfField as string
                                        };
                                    });

                                    setMappingRows(newRows);
                                }
                            }, [formikProps.values[name]]);

                            // Updates the actual form field value based on the UI state
                            const updateFieldValue = (rows: MappingRows) => {
                                const newValue: Record<string, string> = {};

                                // For each valid row (has both column and adf_field), add to the value object
                                Object.values(rows).forEach((row) => {
                                    if (row.column && row.adf_field) {
                                        newValue[row.column] = row.adf_field;
                                    }
                                });

                                formikProps.setFieldValue(name, newValue);
                            };

                            const handleAddMapping = () => {
                                const newRowId = `row-${Math.random().toString(36).substr(2, 9)}`;
                                const newRows = {
                                    ...mappingRows,
                                    [newRowId]: { column: "", adf_field: "" }
                                };
                                setMappingRows(newRows);
                            };

                            const handleDeleteMapping = (rowId: string) => {
                                const newRows = { ...mappingRows };
                                delete newRows[rowId];
                                setMappingRows(newRows);
                                updateFieldValue(newRows);
                            };

                            const handleColumnChange = (rowId: string, columnValue: string) => {
                                const newRows = { ...mappingRows };
                                newRows[rowId] = {
                                    ...newRows[rowId],
                                    column: columnValue
                                };
                                setMappingRows(newRows);
                                updateFieldValue(newRows);
                            };

                            const handleAdfFieldChange = (rowId: string, adfFieldValue: string) => {
                                const newRows = { ...mappingRows };
                                newRows[rowId] = {
                                    ...newRows[rowId],
                                    adf_field: adfFieldValue
                                };
                                setMappingRows(newRows);
                                updateFieldValue(newRows);
                            };

                            return (
                                <div>
                                    <div className="flex justify-start mb-3 mt-3">
                                        <Button
                                            colorScheme="blue"
                                            size="sm"
                                            leftIcon={<AddIcon />}
                                            onClick={handleAddMapping}
                                            isDisabled={isDropdownDisabled}
                                        >
                                            Add Mapping
                                        </Button>
                                    </div>

                                    {Object.keys(mappingRows).map((rowId) => (
                                        <Box
                                            key={rowId}
                                            className="relative mt-4 mb-4 border-[1px] p-4 rounded-lg border-zinc-500 flex items-center"
                                        >
                                            <div className="flex flex-1 gap-3">
                                                <div className="w-1/2">
                                                    <FormikSelect
                                                        id={`${name}-${rowId}-column`}
                                                        placeholder="Column"
                                                        options={columnOptions}
                                                        className="w-full"
                                                        name={`mapping_${rowId}_column`}
                                                        onChange={(selected: any) => {
                                                            handleColumnChange(
                                                                rowId,
                                                                selected ? selected.value : ""
                                                            );
                                                        }}
                                                        value={columnOptions.find(
                                                            (opt) =>
                                                                opt.value ===
                                                                mappingRows[rowId]?.column
                                                        )}
                                                    />
                                                </div>
                                                <div className="w-1/2">
                                                    <FormikSelect
                                                        id={`${name}-${rowId}-adf-field`}
                                                        placeholder="ADF field"
                                                        options={adfStructureOptions}
                                                        className="w-full"
                                                        isSearchable={true}
                                                        name={`mapping_${rowId}_adf_field`}
                                                        onChange={(selected: any) => {
                                                            handleAdfFieldChange(
                                                                rowId,
                                                                selected ? selected.value : ""
                                                            );
                                                        }}
                                                        value={adfStructureOptions.find(
                                                            (opt) =>
                                                                opt.value ===
                                                                mappingRows[rowId]?.adf_field
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div className="ml-3">
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteMapping(rowId)}
                                                    className="text-red-400"
                                                >
                                                    <BsTrash size={"18px"} />
                                                </button>
                                            </div>
                                        </Box>
                                    ))}
                                </div>
                            );
                        }}
                    </Field>
                    {isInvalid && (
                        <p className="form-errors text-[14px] mt-[4px] text-red-300">
                            {formikProps.errors[name]?.toString()}
                        </p>
                    )}
                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );
        case "number":
            return (
                <FormControl mb={6} isInvalid={isInvalid}>
                    <div className="mb-[8px]">
                        <div className="flex justify-between items-center">
                            <p
                                className={`text-[13px] text-white font-medium flex items-center gap-[4px]`}
                            >
                                <span>{argument.title || argument.label}</span>
                                {argument.required && (
                                    <span className="text-red-400 text-[10.8px]">(Required)</span>
                                )}
                            </p>
                            {!!argument.description && (
                                <IconButton
                                    icon={<IoIosInformationCircleOutline size="18px" />}
                                    aria-label={"Description"}
                                    title="Show Description"
                                    className="cursor-pointer"
                                    size="xs"
                                    variant={"ghost"}
                                    onClick={() =>
                                        setShowDescription((prev) => ({
                                            ...prev,
                                            [name]: !prev[name]
                                        }))
                                    }
                                />
                            )}
                        </div>
                        {argument.description && showDescription[name] && (
                            <p className="text-[13px] font-normal text-neutral-400">
                                {argument.description}
                            </p>
                        )}
                    </div>
                    <Field
                        name={name}
                        {...(argument.required
                            ? {
                                  validate: (value) => validateRequired(value, argument.label)
                              }
                            : {})}
                    >
                        {({ field }) => (
                            <Input
                                {...field} // Formik field props
                                id={name}
                                placeholder={
                                    argument?.placeholder ||
                                    `Enter ${argument.title || argument.label}`
                                }
                                variant="filled"
                                className={`${isInvalid && "border-[#FC8181]"} resize-none overflow-hidden`}
                                onBlur={formikProps.handleBlur}
                                value={formikProps.values[name] ?? ""}
                                minHeight="40px"
                                height="auto"
                                isDisabled={argument.parameter_type === "user"}
                            />
                        )}
                    </Field>
                    {isInvalid && (
                        <p className="form-errors text-[14px] mt-[4px] text-red-300">
                            {formikProps.errors[name]?.toString()}
                        </p>
                    )}
                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );
        case "array":
            return (
                <FormControl mb={6}>
                    <div className="flex gap-[5px] items-center">
                        <Box as="span" flex="1" textAlign="left" className="pb-4">
                            <div className="flex justify-between items-center">
                                <p className="text-[13px] text-white font-medium flex items-center gap-[4px]">
                                    <span>{argument.title || argument.label}</span>
                                    {argument.required && (
                                        <span className="text-red-400 text-[10.8px]">
                                            (Required)
                                        </span>
                                    )}
                                </p>
                                {!!argument.description && (
                                    <IconButton
                                        icon={<IoIosInformationCircleOutline size="18px" />}
                                        aria-label={"Description"}
                                        title="Show Description"
                                        className="cursor-pointer"
                                        size="xs"
                                        variant={"ghost"}
                                        onClick={() =>
                                            setShowDescription((prev) => ({
                                                ...prev,
                                                [name]: !prev[name]
                                            }))
                                        }
                                    />
                                )}
                            </div>
                            {argument.description && showDescription[name] && (
                                <p className="text-[13px] font-normal text-neutral-400">
                                    {argument.description}
                                </p>
                            )}
                        </Box>
                    </div>

                    <FieldArray name={name}>
                        {({ remove, push }) => (
                            <Box mb={4}>
                                <div className="mb-3">
                                    <Button
                                        colorScheme="blue"
                                        size="sm"
                                        leftIcon={<AddIcon />}
                                        onClick={() => {
                                            // If item_type is object and we have an items structure, create an empty object with defined properties
                                            if (
                                                argument.item_type === "object" &&
                                                argument.items?.properties
                                            ) {
                                                // Create a properly initialized empty item
                                                const emptyItem = {};

                                                // Initialize each property with an empty value based on its type
                                                Object.keys(argument.items.properties).forEach(
                                                    (propKey) => {
                                                        const propConfig =
                                                            argument.items.properties[propKey];
                                                        if (propConfig.type === "array") {
                                                            emptyItem[propKey] = [];
                                                        } else if (propConfig.type === "object") {
                                                            emptyItem[propKey] = {};
                                                        } else {
                                                            emptyItem[propKey] = "";
                                                        }
                                                    }
                                                );

                                                // First add the item to the array
                                                const currentValues = [
                                                    ...(formikProps.values[name] || [])
                                                ];
                                                currentValues.push(emptyItem);

                                                // Update the array in Formik
                                                formikProps.setFieldValue(name, currentValues);

                                                // Clear any leftover form values from previous edits
                                                if (argument.items?.properties) {
                                                    Object.keys(argument.items.properties).forEach(
                                                        (propKey) => {
                                                            formikProps.setFieldValue(propKey, "");
                                                        }
                                                    );
                                                }

                                                // The new item index is the last one in the array
                                                const newIndex = currentValues.length - 1;

                                                // Set the editing index and open modal
                                                setEditingIndex(newIndex);
                                                setIsModalOpen(true);
                                            } else {
                                                // Original behavior for primitive types
                                                push("");
                                            }
                                        }}
                                    >
                                        Add Item
                                    </Button>
                                </div>

                                {formikProps.values[name] &&
                                    formikProps.values[name].length > 0 &&
                                    formikProps.values[name].map((item, index) => (
                                        <Box
                                            key={`${name}-${index}`}
                                            className="relative mt-4 mb-4 border-[1px] p-2 rounded-lg border-zinc-500"
                                        >
                                            {argument.item_type === "object" &&
                                            argument.items?.properties ? (
                                                <Card
                                                    name={
                                                        item &&
                                                        typeof item === "object" &&
                                                        Object.keys(item).length > 0
                                                            ? (() => {
                                                                  // Check if this object has a transaction_events property with an event_name
                                                                  const txEvent =
                                                                      item.transaction_events;
                                                                  if (
                                                                      txEvent &&
                                                                      typeof txEvent === "object" &&
                                                                      txEvent.event_name
                                                                  ) {
                                                                      const seq =
                                                                          item.sequence_number;
                                                                      return `${txEvent.event_name}${
                                                                          seq !== undefined &&
                                                                          seq !== null
                                                                              ? ` (Sequence #${seq})`
                                                                              : ""
                                                                      }`;
                                                                  }
                                                                  // Check if this object has a transaction_catalogue property with nested event
                                                                  const txCatalogue =
                                                                      item.transaction_catalogue;
                                                                  const txEventInCatalogue =
                                                                      txCatalogue?.transaction_coordinator_event;
                                                                  if (
                                                                      txCatalogue &&
                                                                      typeof txCatalogue ===
                                                                          "object" &&
                                                                      txEventInCatalogue &&
                                                                      typeof txEventInCatalogue ===
                                                                          "object" &&
                                                                      txEventInCatalogue.event_name
                                                                  ) {
                                                                      return txEventInCatalogue.event_name;
                                                                  }
                                                                  // Fall back to the generic "(X properties)" format
                                                                  return `${argument.label || "Item"} (${Object.keys(item).length} properties)`;
                                                              })()
                                                            : argument.label || "Array Item"
                                                    }
                                                    onDelete={() => {
                                                        remove(index);
                                                    }}
                                                    onConfigure={() => {
                                                        setEditingIndex(index);

                                                        // Clear previous values first
                                                        if (argument.items?.properties) {
                                                            Object.keys(
                                                                argument.items.properties
                                                            ).forEach((propKey) => {
                                                                formikProps.setFieldValue(
                                                                    propKey,
                                                                    ""
                                                                );
                                                            });
                                                        }

                                                        // Then populate with current item's values
                                                        if (item && typeof item === "object") {
                                                            Object.entries(item).forEach(
                                                                ([propKey, propValue]) => {
                                                                    if (Array.isArray(propValue)) {
                                                                        formikProps.setFieldValue(
                                                                            propKey,
                                                                            [...propValue]
                                                                        );
                                                                    } else if (
                                                                        typeof propValue ===
                                                                            "object" &&
                                                                        propValue !== null
                                                                    ) {
                                                                        formikProps.setFieldValue(
                                                                            propKey,
                                                                            { ...propValue }
                                                                        );
                                                                    } else {
                                                                        formikProps.setFieldValue(
                                                                            propKey,
                                                                            propValue
                                                                        );
                                                                    }
                                                                }
                                                            );
                                                        }

                                                        setIsModalOpen(true);
                                                    }}
                                                />
                                            ) : (
                                                // Original behavior for primitive type items
                                                <div className="flex items-center">
                                                    <FormControl className="flex-1">
                                                        <Field
                                                            as={Input}
                                                            type={argument.item_type || "string"}
                                                            name={`${name}.${index}`}
                                                            value={item}
                                                        />
                                                    </FormControl>
                                                    <div className="ml-3">
                                                        <button
                                                            type="button"
                                                            onClick={() => remove(index)}
                                                            className="text-red-400"
                                                        >
                                                            <BsTrash size={"18px"} />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </Box>
                                    ))}
                            </Box>
                        )}
                    </FieldArray>

                    {/* Modal for editing objects */}
                    {argument.item_type === "object" && argument.items?.properties && (
                        <ObjectModal
                            isOpen={isModalOpen}
                            onClose={() => {
                                setIsModalOpen(false);
                                setEditingIndex(null);
                            }}
                            argument={{
                                ...argument.items,
                                key: editingIndex !== null ? `${name}.${editingIndex}` : "",
                                label: argument.label || "Array Item"
                            }}
                            formikProps={formikProps}
                            editMode={editingIndex !== null}
                            onChange={(result) => {
                                if (editingIndex !== null) {
                                    // Update the array item with the result from the modal
                                    const resultKey = `${name}.${editingIndex}`;
                                    const updatedItem = result[resultKey];

                                    if (updatedItem) {
                                        // Make a deep copy of the current array value to avoid mutation issues
                                        const newValues = [...(formikProps.values[name] || [])];

                                        // Create a clean copy of the existing item (if any) to maintain other properties
                                        const existingItem = newValues[editingIndex] || {};

                                        // Merge the updated values with any existing values
                                        newValues[editingIndex] = {
                                            ...existingItem,
                                            ...updatedItem
                                        };

                                        // Set the entire array back to Formik
                                        formikProps.setFieldValue(name, newValues);

                                        console.log("Updated array item:", newValues[editingIndex]);
                                        console.log("Full array:", newValues);
                                    } else {
                                        // If no updatedItem was found with the expected key structure,
                                        // the result might be structured differently - try to parse it

                                        // Get all form values that were set in the modal
                                        const formValues = {};

                                        if (argument.items?.properties) {
                                            Object.keys(argument.items.properties).forEach(
                                                (key) => {
                                                    if (formikProps.values[key] !== undefined) {
                                                        formValues[key] = formikProps.values[key];
                                                    }
                                                }
                                            );
                                        }

                                        // If we have values, update the array item
                                        if (Object.keys(formValues).length > 0) {
                                            const newValues = [...(formikProps.values[name] || [])];
                                            const existingItem = newValues[editingIndex] || {};

                                            newValues[editingIndex] = {
                                                ...existingItem,
                                                ...formValues
                                            };

                                            formikProps.setFieldValue(name, newValues);

                                            console.log(
                                                "Updated array item (alternate):",
                                                newValues[editingIndex]
                                            );
                                            console.log("Full array (alternate):", newValues);
                                        }
                                    }
                                }

                                // Reset the editing state
                                setIsModalOpen(false);
                                setEditingIndex(null);
                            }}
                        />
                    )}

                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );
        case "object":
            return (
                <FormControl mb={6}>
                    <div className="flex gap-[5px] items-center">
                        <Box as="span" flex="1" textAlign="left" className="pb-4">
                            <div className="flex justify-between items-center">
                                <p className="text-[13px] text-white font-medium flex items-center gap-[4px]">
                                    <span>{argument.title || argument.label}</span>
                                    {argument.required && (
                                        <span className="text-red-400 text-[10.8px]">
                                            (Required)
                                        </span>
                                    )}
                                </p>
                                {!!argument.description && (
                                    <IconButton
                                        icon={<IoIosInformationCircleOutline size="18px" />}
                                        aria-label={"Description"}
                                        title="Show Description"
                                        className="cursor-pointer"
                                        size="xs"
                                        variant={"ghost"}
                                        onClick={() =>
                                            setShowDescription((prev) => ({
                                                ...prev,
                                                [name]: !prev[name]
                                            }))
                                        }
                                    />
                                )}
                            </div>
                            {argument.description && showDescription[name] && (
                                <p className="text-[13px] font-normal text-neutral-400">
                                    {argument.description}
                                </p>
                            )}
                        </Box>
                    </div>

                    {argument?.button_text ? (
                        <Button
                            leftIcon={<AddIcon />}
                            onClick={() => {
                                if (!isEditing) {
                                    handleOpenModal();
                                }
                            }}
                            size="sm"
                            colorScheme="blue"
                            mt={4}
                        >
                            {argument.button_text}
                        </Button>
                    ) : (
                        !hasItem && (
                            <Button
                                leftIcon={<AddIcon />}
                                onClick={() => {
                                    if (!isEditing) {
                                        handleOpenModal();
                                    }
                                }}
                                size="sm"
                                colorScheme="blue"
                                mt={4}
                            >
                                Configure
                            </Button>
                        )
                    )}

                    <ObjectModal
                        isOpen={isModalOpen}
                        onClose={handleCloseModal}
                        argument={argument}
                        formikProps={formikProps}
                        editMode={isEditing}
                        onChange={(result) => {
                            if (argument.key_placeholder) {
                                const dynamicKey = formikProps.values[argument.key_placeholder];
                                const newCardValue = result[argument.key][dynamicKey];
                                // Create a copy of the current object for this field
                                const updatedObject = {
                                    ...(formikProps.values[argument.key] || {})
                                };

                                if (isEditing && editingKey) {
                                    // If the key has changed, remove the old key.
                                    if (
                                        editingKey !== dynamicKey &&
                                        Object.prototype.hasOwnProperty.call(
                                            updatedObject,
                                            editingKey
                                        )
                                    ) {
                                        delete updatedObject[editingKey];
                                    }
                                    // Update with the new dynamic key
                                    updatedObject[dynamicKey] = newCardValue;
                                } else {
                                    // In non-edit mode, simply add the new key/value pair.
                                    updatedObject[dynamicKey] = newCardValue;
                                }

                                // Update state and Formik's field value with the updated object.
                                setObjectResult(updatedObject);
                                formikProps.setFieldValue(argument.key, updatedObject);
                                setIsEditing(false);
                                setEditingKey(null);
                            } else {
                                // Single object branch remains unchanged.
                                if (objectResult && !isEditing) {
                                    hotToast.error("Only one object can exist");
                                } else {
                                    setObjectResult(result[argument.key]);
                                    formikProps.setFieldValue(argument.key, result[argument.key]);
                                }
                                setIsEditing(false);
                            }
                        }}
                    />

                    {argument.key_placeholder
                        ? // Multiple cards branch
                          objectResult && (
                              <div className="flex flex-col space-y-4 mt-4">
                                  {Object.entries(objectResult).map(([key, value]) => (
                                      <Card
                                          key={key}
                                          name={key}
                                          onDelete={() => {
                                              setObjectResult((prev: any) => {
                                                  const newResult = {
                                                      ...prev
                                                  };
                                                  delete newResult[key];
                                                  return newResult;
                                              });
                                              const updatedField = {
                                                  ...(formikProps.values[argument.key] || {})
                                              };
                                              delete updatedField[key];
                                              formikProps.setFieldValue(argument.key, updatedField);
                                          }}
                                          onConfigure={() => {
                                              setIsEditing(true);
                                              setEditingKey(key);
                                              formikProps.setFieldValue(
                                                  argument.key_placeholder!,
                                                  key
                                              );
                                              if (value && typeof value === "object") {
                                                  Object.entries(value).forEach(([prop, val]) => {
                                                      // Special handling for arrays - initialize the array value
                                                      if (Array.isArray(val)) {
                                                          formikProps.setFieldValue(prop, [...val]);
                                                      } else {
                                                          formikProps.setFieldValue(prop, val);
                                                      }
                                                  });
                                              } else {
                                                  // If value is not an object, create an object with a "value_schema" key.
                                                  formikProps.setFieldValue("value_schema", value);
                                              }
                                              handleOpenModal();
                                          }}
                                      />
                                  ))}
                              </div>
                          )
                        : // Single object branch
                          objectResult && (
                              <div className="mt-4">
                                  <Card
                                      name={argument.key}
                                      onDelete={() => {
                                          setObjectResult(null);
                                          formikProps.setFieldValue(argument.key, null);
                                      }}
                                      onConfigure={() => {
                                          setIsEditing(true);
                                          Object.entries(objectResult).forEach(([prop, val]) => {
                                              if (val && typeof val === "object") {
                                                  if (Array.isArray(val)) {
                                                      // Special handling for arrays - initialize the array value
                                                      formikProps.setFieldValue(prop, [...val]);
                                                  } else {
                                                      Object.entries(val).forEach(
                                                          ([nestedKey, nestedVal]) => {
                                                              if (Array.isArray(nestedVal)) {
                                                                  // Handle nested arrays
                                                                  formikProps.setFieldValue(
                                                                      nestedKey,
                                                                      [...nestedVal]
                                                                  );
                                                              } else {
                                                                  formikProps.setFieldValue(
                                                                      nestedKey,
                                                                      nestedVal
                                                                  );
                                                              }
                                                          }
                                                      );
                                                  }
                                              } else {
                                                  formikProps.setFieldValue(prop, val);
                                              }
                                          });
                                          handleOpenModal();
                                      }}
                                  />
                              </div>
                          )}
                </FormControl>
            );
        case "time_zone":
            return (
                <FormControl mb={6} isInvalid={isInvalid}>
                    <div className="mb-[8px]">
                        <div className="flex justify-between items-center">
                            <p className="text-[13px] text-white font-medium flex items-center gap-[4px]">
                                <span>{argument.title || argument.label}</span>
                                {argument.required && (
                                    <span className="text-red-400 text-[10.8px]">(Required)</span>
                                )}
                            </p>
                            {!!argument.description && (
                                <IconButton
                                    icon={<IoIosInformationCircleOutline size="18px" />}
                                    aria-label={"Description"}
                                    title="Show Description"
                                    className="cursor-pointer"
                                    size="xs"
                                    variant={"ghost"}
                                    onClick={() =>
                                        setShowDescription((prev) => ({
                                            ...prev,
                                            [name]: !prev[name]
                                        }))
                                    }
                                />
                            )}
                        </div>
                        {argument.description && showDescription[name] && (
                            <p className="text-[13px] font-normal text-neutral-400">
                                {argument.description}
                            </p>
                        )}
                    </div>
                    <Field
                        name={name}
                        {...(argument.required
                            ? {
                                  validate: (value) => validateRequired(value, argument.label)
                              }
                            : {})}
                    >
                        {({ field }) => {
                            // Find the matching timezone option based on the stored value
                            const selectedValue = formikProps.values[name];
                            let selectedOption: any = undefined;

                            if (selectedValue) {
                                selectedOption = selectedValue;
                            } else if (
                                formikProps.values.label &&
                                formikProps.values.value &&
                                formikProps.values.offset
                            ) {
                                selectedOption = {
                                    label: formikProps.values.label,
                                    value: formikProps.values.value,
                                    offset: formikProps.values.offset
                                };
                            }

                            return (
                                <FormikSelect
                                    {...field}
                                    id={name}
                                    placeholder={`Select ${argument.title || argument.label}`}
                                    options={timezones}
                                    className={`${isInvalid && "border-[#FC8181]"}`}
                                    onChange={(selectedOption) =>
                                        formikProps.setFieldValue(name, selectedOption)
                                    }
                                    onBlur={formikProps.handleBlur}
                                    value={selectedOption}
                                />
                            );
                        }}
                    </Field>
                    {isInvalid && (
                        <p className="form-errors text-[14px] mt-[4px] text-red-300">
                            {formikProps.errors[name]?.toString()}
                        </p>
                    )}
                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );
        case "dropdown":
            return (
                <FormControl mb={6} isInvalid={isInvalid}>
                    <div className="mb-[8px]">
                        <div className="flex justify-between items-center">
                            <p className="text-[13px] text-white font-medium flex items-center gap-[4px]">
                                <span>{argument.title || argument.label}</span>
                                {argument.required && (
                                    <span className="text-red-400 text-[10.8px]">(Required)</span>
                                )}
                            </p>
                            {!!argument.description && (
                                <IconButton
                                    icon={<IoIosInformationCircleOutline size="18px" />}
                                    aria-label={"Description"}
                                    title="Show Description"
                                    className="cursor-pointer"
                                    size="xs"
                                    variant={"ghost"}
                                    onClick={() =>
                                        setShowDescription((prev) => ({
                                            ...prev,
                                            [name]: !prev[name]
                                        }))
                                    }
                                />
                            )}
                        </div>
                        {argument.description && showDescription[name] && (
                            <p className="text-[13px] font-normal text-neutral-400">
                                {argument.description}
                            </p>
                        )}
                    </div>
                    <Field
                        name={name}
                        {...(argument.required
                            ? {
                                  validate: (value) => validateRequired(value, argument.label)
                              }
                            : {})}
                    >
                        {({ field }) => {
                            // Convert string array to options format
                            const dropdownOptions = argument.options
                                ? argument.options.map((option: string) => ({
                                      label: option,
                                      value: option
                                  }))
                                : [];

                            const selectedOption = dropdownOptions.find(
                                (option) => option.value === formikProps.values[name]
                            );

                            return (
                                <FormikSelect
                                    {...field}
                                    id={name}
                                    placeholder={`Select ${argument.title || argument.label}`}
                                    options={dropdownOptions}
                                    className={`${isInvalid && "border-[#FC8181]"}`}
                                    onChange={(selected: any) =>
                                        formikProps.setFieldValue(
                                            name,
                                            selected ? selected.value : ""
                                        )
                                    }
                                    onBlur={formikProps.handleBlur}
                                    value={selectedOption}
                                    isDisabled={argument.parameter_type === "user"}
                                />
                            );
                        }}
                    </Field>
                    {isInvalid && (
                        <p className="form-errors text-[14px] mt-[4px] text-red-300">
                            {formikProps.errors[name]?.toString()}
                        </p>
                    )}
                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );
        case "time":
            return (
                <FormControl mb={6} isInvalid={isInvalid}>
                    <div className="mb-[8px]">
                        <div className="flex justify-between items-center">
                            <p className="text-[13px] text-white font-medium flex items-center gap-[4px]">
                                <span>{argument.title || argument.label}</span>
                                {argument.required && (
                                    <span className="text-red-400 text-[10.8px]">(Required)</span>
                                )}
                            </p>
                            {!!argument.description && (
                                <IconButton
                                    icon={<IoIosInformationCircleOutline size="18px" />}
                                    aria-label={"Description"}
                                    title="Show Description"
                                    className="cursor-pointer"
                                    size="xs"
                                    variant={"ghost"}
                                    onClick={() =>
                                        setShowDescription((prev) => ({
                                            ...prev,
                                            [name]: !prev[name]
                                        }))
                                    }
                                />
                            )}
                        </div>
                        {argument.description && showDescription[name] && (
                            <p className="text-[13px] font-normal text-neutral-400">
                                {argument.description}
                            </p>
                        )}
                    </div>
                    <Field
                        name={name}
                        {...(argument.required
                            ? {
                                  validate: (value) => validateRequired(value, argument.label)
                              }
                            : {})}
                    >
                        {() => {
                            const [isOpen, setIsOpen] = useState(false);
                            const [selectedHour, setSelectedHour] = useState("00");
                            const [selectedMinute, setSelectedMinute] = useState("00");
                            const dropdownRef = useRef<HTMLDivElement>(null);
                            const inputRef = useRef<HTMLInputElement>(null);

                            const hours = Array.from({ length: 24 }, (_, i) =>
                                i.toString().padStart(2, "0")
                            );
                            const minutes = [
                                "00",
                                "05",
                                "10",
                                "15",
                                "20",
                                "25",
                                "30",
                                "35",
                                "40",
                                "45",
                                "50",
                                "55"
                            ];

                            useEffect(() => {
                                const currentValue = formikProps.values[name] || "";
                                if (currentValue) {
                                    const [hour, minute] = currentValue.split(":");
                                    setSelectedHour(hour || "00");
                                    setSelectedMinute(minute || "00");
                                }
                            }, [formikProps.values[name]]);

                            useEffect(() => {
                                function handleClickOutside(event: MouseEvent) {
                                    if (
                                        dropdownRef.current &&
                                        !dropdownRef.current.contains(event.target as Node) &&
                                        inputRef.current &&
                                        !inputRef.current.contains(event.target as Node)
                                    ) {
                                        setIsOpen(false);
                                    }
                                }

                                document.addEventListener("mousedown", handleClickOutside);
                                return () => {
                                    document.removeEventListener("mousedown", handleClickOutside);
                                };
                            }, []);

                            const handleInputClick = () => {
                                if (argument.parameter_type !== "user") {
                                    setIsOpen(!isOpen);
                                }
                            };

                            const handleConfirm = () => {
                                formikProps.setFieldValue(
                                    name,
                                    `${selectedHour}:${selectedMinute}`
                                );
                                setIsOpen(false);
                            };

                            return (
                                <div className="relative w-full">
                                    <div className="relative">
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            className="w-full px-3 py-2 bg-[rgb(82,82,82)] border border-gray-600 rounded text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            placeholder="HH:MM"
                                            value={formikProps.values[name] || ""}
                                            onClick={handleInputClick}
                                            readOnly
                                            disabled={argument.parameter_type === "user"}
                                        />
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <circle cx="12" cy="12" r="10" strokeWidth="2" />
                                            <path strokeWidth="2" d="M12 6v6l4 2" />
                                        </svg>
                                    </div>

                                    {isOpen && (
                                        <div
                                            ref={dropdownRef}
                                            className="absolute mt-1 w-full bg-[rgb(82,82,82)] border border-gray-600 rounded shadow-lg z-10"
                                        >
                                            <div>
                                                <div className="flex border-b border-gray-600">
                                                    <div className="flex-1 border-r border-gray-600 py-2 text-center text-xs font-medium text-gray-300">
                                                        Hour
                                                    </div>
                                                    <div className="flex-1 py-2 text-center text-xs font-medium text-gray-300">
                                                        Minute
                                                    </div>
                                                </div>

                                                <div className="flex">
                                                    <div className="flex-1 border-r border-gray-600 h-48 overflow-y-auto">
                                                        {hours.map((hour) => (
                                                            <div
                                                                key={hour}
                                                                className={`px-4 py-2 cursor-pointer hover:bg-[rgb(65,65,65)] ${
                                                                    selectedHour === hour
                                                                        ? "bg-[rgb(60,60,60)]"
                                                                        : ""
                                                                }`}
                                                                onClick={() =>
                                                                    setSelectedHour(hour)
                                                                }
                                                            >
                                                                {hour}
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className="flex-1 h-48 overflow-y-auto">
                                                        {minutes.map((minute) => (
                                                            <div
                                                                key={minute}
                                                                className={`px-4 py-2 cursor-pointer hover:bg-[rgb(65,65,65)] ${
                                                                    selectedMinute === minute
                                                                        ? "bg-[rgb(60,60,60)]"
                                                                        : ""
                                                                }`}
                                                                onClick={() =>
                                                                    setSelectedMinute(minute)
                                                                }
                                                            >
                                                                {minute}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="border-t border-gray-600 p-2 text-right">
                                                <button
                                                    className="px-4 py-1 text-white-400 hover:text-white-300 focus:outline-none"
                                                    onClick={handleConfirm}
                                                    type="button"
                                                >
                                                    OK
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        }}
                    </Field>
                    {isInvalid && (
                        <p className="form-errors text-[14px] mt-[4px] text-red-300">
                            {formikProps.errors[name]?.toString()}
                        </p>
                    )}
                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );
        case "single_client":
            return (
                <FormControl mb={6} isInvalid={isInvalid}>
                    <div className="mb-[8px]">
                        <div className="flex justify-between items-center">
                            <p
                                className={`text-[13px] text-white font-medium flex items-center gap-[4px]`}
                            >
                                <span>{argument.title || argument.label}</span>
                                {argument.required && (
                                    <span className="text-red-400 text-[10.8px]">(Required)</span>
                                )}
                            </p>
                            {!!argument.description && (
                                <IconButton
                                    icon={<IoIosInformationCircleOutline size="18px" />}
                                    aria-label={"Description"}
                                    title="Show Description"
                                    className="cursor-pointer"
                                    size="xs"
                                    variant={"ghost"}
                                    onClick={() =>
                                        setShowDescription((prev) => ({
                                            ...prev,
                                            [name]: !prev[name]
                                        }))
                                    }
                                />
                            )}
                        </div>
                        {argument.description && showDescription[name] && (
                            <p className="text-[13px] font-normal text-neutral-400">
                                {argument.description}
                            </p>
                        )}
                    </div>
                    <Field
                        name={name}
                        {...(argument.required
                            ? {
                                  validate: (value) => validateRequired(value, argument.label)
                              }
                            : {})}
                    >
                        {({ field }) => {
                            // Find the selected client option based on stored ID
                            const selectedId = formikProps.values[name];
                            const selectedOption = selectedId
                                ? clientOptions.find((opt) => opt.value === selectedId)
                                : null;

                            return (
                                <FormikSelect
                                    {...field}
                                    id={name}
                                    placeholder={`Select ${argument.title || argument.label}`}
                                    options={clientOptions}
                                    className={`${isInvalid && "border-[#FC8181]"}`}
                                    isDisabled={argument.is_disabled}
                                    onChange={(selected) => {
                                        if (selected && typeof selected === "object") {
                                            const option = selected as { value: string };
                                            formikProps.setFieldValue(name, option.value);
                                        } else {
                                            formikProps.setFieldValue(name, null);
                                        }
                                    }}
                                    onBlur={formikProps.handleBlur}
                                    value={selectedOption}
                                />
                            );
                        }}
                    </Field>
                    {isInvalid && (
                        <p className="form-errors text-[14px] mt-[4px] text-red-300">
                            {formikProps.errors[name]?.toString()}
                        </p>
                    )}
                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );
        case "text_area":
            return (
                <FormControl mb={6} isInvalid={isInvalid}>
                    <div className="mb-[8px]">
                        <div className="flex justify-between items-center">
                            <p
                                className={`text-[13px] text-white font-medium flex items-center gap-[4px]`}
                            >
                                <span>{argument.title || argument.label}</span>
                                {argument.required && (
                                    <span className="text-red-400 text-[10.8px]">(Required)</span>
                                )}
                            </p>
                            {!!argument.description && (
                                <IconButton
                                    icon={<IoIosInformationCircleOutline size="18px" />}
                                    aria-label={"Description"}
                                    title="Show Description"
                                    className="cursor-pointer"
                                    size="xs"
                                    variant={"ghost"}
                                    onClick={() =>
                                        setShowDescription((prev) => ({
                                            ...prev,
                                            [name]: !prev[name]
                                        }))
                                    }
                                />
                            )}
                        </div>
                        {argument.description && showDescription[name] && (
                            <p className="text-[13px] font-normal text-neutral-400">
                                {argument.description}
                            </p>
                        )}
                    </div>
                    <Field
                        name={name}
                        {...(argument.required
                            ? {
                                  validate: (value) =>
                                      validateRequired(value, argument.title || argument.label)
                              }
                            : {})}
                    >
                        {({ field }) => (
                            <Input
                                {...field}
                                ref={textareaRef}
                                id={name}
                                placeholder={
                                    argument?.placeholder ||
                                    `Enter ${argument.title || argument.label}`
                                }
                                variant="filled"
                                className={`${isInvalid && "border-[#FC8181]"} resize-none overflow-hidden py-3`}
                                onBlur={formikProps.handleBlur}
                                value={formikProps.values[name] ?? ""}
                                minHeight="100px"
                                height="auto"
                                isDisabled={argument.parameter_type === "user"}
                                as="textarea"
                            />
                        )}
                    </Field>
                    {isInvalid && (
                        <p className="form-errors text-[14px] mt-[4px] text-red-300">
                            {formikProps.errors[name]?.toString()}
                        </p>
                    )}
                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );
        case "transaction_coordinator_events":
            return (
                <FormControl mb={6} isInvalid={isInvalid}>
                    <div className="mb-[8px]">
                        <div className="flex justify-between items-center">
                            <p
                                className={`text-[13px] text-white font-medium flex items-center gap-[4px]`}
                            >
                                <span>{argument.title || argument.label}</span>
                                {argument.required && (
                                    <span className="text-red-400 text-[10.8px]">(Required)</span>
                                )}
                            </p>
                            {!!argument.description && (
                                <IconButton
                                    icon={<IoIosInformationCircleOutline size="18px" />}
                                    aria-label={"Description"}
                                    title="Show Description"
                                    className="cursor-pointer"
                                    size="xs"
                                    variant={"ghost"}
                                    onClick={() =>
                                        setShowDescription((prev) => ({
                                            ...prev,
                                            [name]: !prev[name]
                                        }))
                                    }
                                />
                            )}
                        </div>
                        {argument.description && showDescription[name] && (
                            <p className="text-[13px] font-normal text-neutral-400">
                                {argument.description}
                            </p>
                        )}
                    </div>
                    <Field
                        name={name}
                        {...(argument.required
                            ? {
                                  validate: (value) =>
                                      validateRequired(value, argument.title || argument.label)
                              }
                            : {})}
                    >
                        {({ field }) => {
                            // Find the option that matches the stored value
                            const storedValue = formikProps.values[name];
                            const selectedOption = storedValue
                                ? eventOptions.find(
                                      (opt) =>
                                          opt.value &&
                                          storedValue &&
                                          opt.value.event_id === storedValue.event_id
                                  )
                                : null;

                            return (
                                <FormikSelect
                                    {...field}
                                    id={name}
                                    placeholder={`Select ${argument.title || argument.label}`}
                                    options={eventOptions}
                                    className={`${isInvalid && "border-[#FC8181]"}`}
                                    isDisabled={argument.is_disabled}
                                    onChange={(selected) => {
                                        // Store only the value (event object)
                                        if (selected && typeof selected === "object") {
                                            const option = selected as {
                                                value: { event_id: string; columns?: any };
                                                label: string;
                                            };

                                            // Store only the value (event object)
                                            formikProps.setFieldValue(name, option.value);

                                            // Extract column names (keys) from the selected event
                                            if (
                                                option.value.columns &&
                                                typeof option.value.columns === "object"
                                            ) {
                                                const columnNames = Object.keys(
                                                    option.value.columns
                                                );

                                                // Update using spread to preserve other entries
                                                const newState = {
                                                    ...columnState,
                                                    [argument.key]: columnNames
                                                };

                                                // Update both states together - merge rather than replace global state
                                                globalColumnState = {
                                                    ...globalColumnState,
                                                    ...newState
                                                };
                                                setColumnState(newState);
                                            }
                                        } else {
                                            formikProps.setFieldValue(name, null);

                                            // Clear only this argument's entry from column state
                                            const newState = { ...columnState };
                                            delete newState[argument.key];

                                            // Update local state
                                            setColumnState(newState);

                                            // Update global state preserving other entries
                                            const updatedGlobalState = { ...globalColumnState };
                                            delete updatedGlobalState[argument.key];
                                            globalColumnState = updatedGlobalState;
                                        }
                                    }}
                                    onBlur={formikProps.handleBlur}
                                    value={selectedOption}
                                />
                            );
                        }}
                    </Field>
                    {isInvalid && (
                        <p className="form-errors text-[14px] mt-[4px] text-red-300">
                            {formikProps.errors[name]?.toString()}
                        </p>
                    )}
                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );

        case "transaction_coordinator_catalogue":
            return (
                <FormControl mb={6} isInvalid={isInvalid}>
                    <div className="mb-[8px]">
                        <div className="flex justify-between items-center">
                            <p
                                className={`text-[13px] text-white font-medium flex items-center gap-[4px]`}
                            >
                                <span>{argument.title || argument.label}</span>
                                {argument.required && (
                                    <span className="text-red-400 text-[10.8px]">(Required)</span>
                                )}
                            </p>
                            {!!argument.description && (
                                <IconButton
                                    icon={<IoIosInformationCircleOutline size="18px" />}
                                    aria-label={"Description"}
                                    title="Show Description"
                                    className="cursor-pointer"
                                    size="xs"
                                    variant={"ghost"}
                                    onClick={() =>
                                        setShowDescription((prev) => ({
                                            ...prev,
                                            [name]: !prev[name]
                                        }))
                                    }
                                />
                            )}
                        </div>
                        {argument.description && showDescription[name] && (
                            <p className="text-[13px] font-normal text-neutral-400">
                                {argument.description}
                            </p>
                        )}
                    </div>
                    <Field
                        name={name}
                        {...(argument.required
                            ? {
                                  validate: (value) =>
                                      validateRequired(value, argument.title || argument.label)
                              }
                            : {})}
                    >
                        {({ field }) => {
                            // Find the option that matches the stored value
                            const storedValue = formikProps.values[name];
                            const selectedOption = storedValue
                                ? catalogueOptions.find(
                                      (opt) =>
                                          opt.value &&
                                          storedValue &&
                                          opt.value.transaction_coordinator_event?.event_id ===
                                              storedValue.transaction_coordinator_event?.event_id
                                  )
                                : null;

                            return (
                                <FormikSelect
                                    {...field}
                                    id={name}
                                    placeholder={`Select ${argument.title || argument.label}`}
                                    options={catalogueOptions}
                                    className={`${isInvalid && "border-[#FC8181]"}`}
                                    isDisabled={argument.is_disabled}
                                    onChange={(selected) => {
                                        // Store only the value (catalogue object)
                                        if (selected && typeof selected === "object") {
                                            const option = selected as {
                                                value: {
                                                    transaction_coordinator_event: {
                                                        event_id: string;
                                                    };
                                                    columns?: any;
                                                };
                                                label: string;
                                            };

                                            // Store only the value (catalogue object)
                                            formikProps.setFieldValue(name, option.value);

                                            // Extract column names (keys) from the selected catalogue
                                            if (
                                                option.value.columns &&
                                                typeof option.value.columns === "object"
                                            ) {
                                                const columnNames = Object.keys(
                                                    option.value.columns
                                                );

                                                // Update using spread to preserve other entries
                                                const newState = {
                                                    ...columnState,
                                                    [argument.key]: columnNames
                                                };

                                                // Update both states together - merge rather than replace global state
                                                globalColumnState = {
                                                    ...globalColumnState,
                                                    ...newState
                                                };
                                                setColumnState(newState);
                                            }
                                        } else {
                                            formikProps.setFieldValue(name, null);

                                            // Clear only this argument's entry from column state
                                            const newState = { ...columnState };
                                            delete newState[argument.key];

                                            // Update local state
                                            setColumnState(newState);

                                            // Update global state preserving other entries
                                            const updatedGlobalState = { ...globalColumnState };
                                            delete updatedGlobalState[argument.key];
                                            globalColumnState = updatedGlobalState;
                                        }
                                    }}
                                    onBlur={formikProps.handleBlur}
                                    value={selectedOption}
                                />
                            );
                        }}
                    </Field>
                    {isInvalid && (
                        <p className="form-errors text-[14px] mt-[4px] text-red-300">
                            {formikProps.errors[name]?.toString()}
                        </p>
                    )}
                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );
        default:
            return (
                <FormControl mb={6} isInvalid={isInvalid}>
                    <div className="mb-[8px]">
                        <div className="flex justify-between items-center">
                            <p
                                className={`text-[13px] text-white font-medium flex items-center gap-[4px]`}
                            >
                                <span>{argument.title || argument.label}</span>
                                {argument.required && (
                                    <span className="text-red-400 text-[10.8px]">(Required)</span>
                                )}
                            </p>
                            {!!argument.description && (
                                <IconButton
                                    icon={<IoIosInformationCircleOutline size="18px" />}
                                    aria-label={"Description"}
                                    title="Show Description"
                                    className="cursor-pointer"
                                    size="xs"
                                    variant={"ghost"}
                                    onClick={() =>
                                        setShowDescription((prev) => ({
                                            ...prev,
                                            [name]: !prev[name]
                                        }))
                                    }
                                />
                            )}
                        </div>
                        {argument.description && showDescription[name] && (
                            <p className="text-[13px] font-normal text-neutral-400">
                                {argument.description}
                            </p>
                        )}
                    </div>
                    <Field
                        name={name}
                        {...(argument.required
                            ? {
                                  validate: (value) => validateRequired(value, argument.label)
                              }
                            : {})}
                    >
                        {({ field }) =>
                            argument.textarea ? (
                                <Input
                                    {...field}
                                    id={name}
                                    placeholder={
                                        argument?.placeholder ||
                                        `Enter ${argument.title || argument.label}`
                                    }
                                    variant="filled"
                                    className={`${isInvalid && "border-[#FC8181]"} resize-none overflow-hidden`}
                                    onBlur={formikProps.handleBlur}
                                    value={formikProps.values[name] ?? ""}
                                    minHeight="100px"
                                    height="auto"
                                    isDisabled={argument.parameter_type === "user"}
                                    as="textarea"
                                />
                            ) : (
                                <Input
                                    {...field}
                                    id={name}
                                    placeholder={
                                        argument?.placeholder ||
                                        `Enter ${argument.title || argument.label}`
                                    }
                                    variant="filled"
                                    className={`${isInvalid && "border-[#FC8181]"} resize-none overflow-hidden`}
                                    onBlur={formikProps.handleBlur}
                                    value={formikProps.values[name] ?? ""}
                                    minHeight="40px"
                                    height="auto"
                                    isDisabled={argument.parameter_type === "user"}
                                />
                            )
                        }
                    </Field>
                    {isInvalid && (
                        <p className="form-errors text-[14px] mt-[4px] text-red-300">
                            {formikProps.errors[name]?.toString()}
                        </p>
                    )}
                    <div className="mt-[8px]">
                        {showPersistCheckbox && (
                            <Checkbox
                                defaultChecked={argument.parameter_type === "user"}
                                onChange={onPersistHandler}
                            >
                                <span className="text-[14px] font-medium text-blue-300">
                                    Convert to user input
                                </span>
                            </Checkbox>
                        )}
                    </div>
                </FormControl>
            );
    }
};

export default FormikFormInputControl;
